import { Component, OnInit } from "@angular/core"
import { AlertData } from "../../models/alertData.model"
import { WindowSizeService } from "../../services/window-size.service"
import { CookiesService } from "../../services/cookies.service"
import { UserService } from "../../services/user.service"
import { AddOnViewService } from "../../services/add-on-view.service"
import { GTMService } from "../../services/gtm.service"
import Utils from "../../utils/utils"

export enum AlertBarCookie {
    HideAlert = "hideAlert",
}

const HAS_DISPLAYED_BANNER_ON_THIS_SESSION_KEY = "has_displayed_banner_on_this_session"

@Component({
    selector: "app-alert-bar",
    templateUrl: "./alert-bar.component.html",
    styleUrls: ["./alert-bar.component.scss"],
    standalone: false,
})
export class AlertBarComponent implements OnInit {
    public showAlert: boolean
    public alertData: AlertData

    private readonly studentCookieKey: string = "showAlertStudent"
    private readonly teacherCookieKey: string = "showAlertTeacher"

    constructor(
        public windowSize: WindowSizeService,
        private cookieService: CookiesService,
        private userService: UserService,
        private addOnService: AddOnViewService,
        private gtmService: GTMService
    ) {}

    private get cookieKey(): string {
        return this.userService.hasStudentExperience()
            ? this.studentCookieKey
            : this.teacherCookieKey
    }

    public ngOnInit() {
        this.loadAlertData()
        this.setComponentVisibility()
    }

    public close() {
        this.gtmService.pushToDataLayer({
            event: "feature_tracking",
            feature_category: "urgent messaging",
            feature_name: "modal",
            object_name: "dismiss button",
            object_action: "click",
            object_type: "button",
            object_text: "",
            object_action_behavior: "dismiss urgent message",
        })
        if (!!this.alertData.should_display_on_every_session) {
            sessionStorage.setItem(HAS_DISPLAYED_BANNER_ON_THIS_SESSION_KEY, "true")
        }
        this.showAlert = false
        this.cookieService.setCookie(
            this.cookieKey,
            AlertBarCookie.HideAlert,
            this.alertData.day_number
        )
    }

    private loadAlertData() {
        if (Utils.hasStudentExperience()) {
            this.alertData = this.userService.studentAlertData
        } else {
            this.alertData = this.userService.teacherAlertData
        }
    }

    private setComponentVisibility() {
        if (this.addOnService.isAddonView && Utils.hasStudentExperience()) this.showAlert = false
        let displayOnEverySession: boolean
        if (this.alertData?.should_display_on_every_session === "true") {
            displayOnEverySession = this.alertData.should_display_on_every_session === "true"
        } else {
            sessionStorage.removeItem(HAS_DISPLAYED_BANNER_ON_THIS_SESSION_KEY)
        }

        if (
            displayOnEverySession &&
            !sessionStorage.getItem(HAS_DISPLAYED_BANNER_ON_THIS_SESSION_KEY)
        )
            sessionStorage.setItem(HAS_DISPLAYED_BANNER_ON_THIS_SESSION_KEY, "false")

        const alertBarCookie = this.cookieService.getCookie(this.cookieKey)
        const currentPathNameNotExcluded =
            this.alertData &&
            this.alertData.page_regex &&
            !new RegExp(this.alertData.page_regex).test(window.location.pathname)
        const userExpressedIntentionToHide =
            alertBarCookie && alertBarCookie.value === AlertBarCookie.HideAlert

        if (
            sessionStorage.getItem(HAS_DISPLAYED_BANNER_ON_THIS_SESSION_KEY) === "false" ||
            (this.alertData?.should_display_on_addon === "true" && this.addOnService.isAddonView)
        ) {
            this.showAlert = true
        } else {
            this.showAlert =
                this.alertData?.should_display_on_addon === "true" &&
                this.alertData &&
                currentPathNameNotExcluded &&
                (!userExpressedIntentionToHide || this.addOnService.isAddonView)
        }

        if (this.showAlert)
            this.gtmService.pushToDataLayer({
                event: "feature_tracking",
                feature_category: "urgent messaging",
                feature_name: "modal",
                object_name: "modal",
                object_action: "impression",
                object_type: "",
                object_text: "",
                object_action_behavior: "display content",
            })
    }

    hasClickedBanner() {
        this.gtmService.pushToDataLayer({
            event: "feature_tracking",
            feature_category: "urgent messaging",
            feature_name: "modal",
            object_name: "cta button",
            object_action: "click",
            object_type: "button",
            object_text: "Contact Congress Now",
            object_action_behavior: "send user to link destination",
        })
        window.open(this.alertData.link, "_blank").focus()
    }
}
